import { useEffect, useRef } from "react";

/**
 * Global keyboard handling.
 *
 * Two rules make this behave the way a mail client is expected to:
 *
 *  - Typing wins. While focus is in a text field the only keys we intercept are
 *    Escape and the Cmd/Ctrl combinations, so "e" in a subject line stays an
 *    "e" instead of archiving the message behind the composer.
 *  - Sequences are real. Gmail-style `g i` needs the `g` to be remembered, but
 *    only briefly — after a second it lapses, so a stray `g` cannot silently
 *    arm a jump that fires on the next unrelated keypress.
 */

const SEQUENCE_TIMEOUT_MS = 1000;

function isTextEntry(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

/**
 * Normalises an event into a comparable string:
 *   "j", "?", "Escape", "mod+Enter", "g i"
 */
function describe(e: KeyboardEvent, pending: string | null): string {
  const mod = e.metaKey || e.ctrlKey ? "mod+" : "";
  const key = e.key;
  if (pending && !mod) return `${pending} ${key}`;
  return `${mod}${key}`;
}

export function useHotkeys(handlers: Record<string, (e: KeyboardEvent) => void>): void {
  // A ref, so re-rendering with fresh closures never detaches and reattaches
  // the listener mid-keystroke.
  const latest = useRef(handlers);
  latest.current = handlers;

  useEffect(() => {
    let pending: string | null = null;
    let timer: number | undefined;

    function clearPending() {
      pending = null;
      window.clearTimeout(timer);
    }

    function onKeyDown(e: KeyboardEvent) {
      const typing = isTextEntry(e.target);
      const mod = e.metaKey || e.ctrlKey;
      if (typing && e.key !== "Escape" && !mod) return;
      if (e.altKey) return;

      const combo = describe(e, pending);
      clearPending();

      const handler = latest.current[combo];
      if (handler) {
        e.preventDefault();
        handler(e);
        return;
      }

      // Not a binding on its own — is it the start of one?
      if (!mod && !typing && Object.keys(latest.current).some((k) => k.startsWith(`${e.key} `))) {
        pending = e.key;
        timer = window.setTimeout(clearPending, SEQUENCE_TIMEOUT_MS);
      }
    }

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.clearTimeout(timer);
    };
  }, []);
}
